/**
 * Trunnion AI - useOnboarding Hook
 * Reads tour completion state from uiStore and exposes helpers to
 * start, skip, or complete the interactive onboarding tour.
 */

import { useCallback } from 'react';
import { useUIStore } from '@/stores/uiStore';

export interface UseOnboardingReturn {
  /** Whether the tour should be displayed (i.e. user has not yet completed it) */
  shouldShowTour: boolean;
  /** Begin the tour (resets the completed flag so the tour becomes visible) */
  startTour: () => void;
  /** Skip the tour without completing it (marks as completed) */
  skipTour: () => void;
  /** Mark the tour as completed */
  completeTour: () => void;
}

export function useOnboarding(): UseOnboardingReturn {
  const tourCompleted = useUIStore((s) => s.tourCompleted);
  const setTourCompleted = useUIStore((s) => s.setTourCompleted);

  const shouldShowTour = !tourCompleted;

  const startTour = useCallback(() => {
    setTourCompleted(false);
  }, [setTourCompleted]);

  const skipTour = useCallback(() => {
    setTourCompleted(true);
  }, [setTourCompleted]);

  const completeTour = useCallback(() => {
    setTourCompleted(true);
  }, [setTourCompleted]);

  return {
    shouldShowTour,
    startTour,
    skipTour,
    completeTour,
  };
}
